import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { EDIT_RECIPE_PATHS, editRecipeStepFullPath, stepsLimitCount, undefinedIndex } from '../../constants';
import { Dispatch, RootState } from '../../../../store/store';
import { connect } from 'react-redux';
import { IActionsDropdown, IActionsTag } from '../../../../types/tableDropdown';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../../types/element-size';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import Table from '../../../../components/Table/Table';
import Modal from '../../../../components/Modal/Modal';
import { IRecipeStep, IReorderedRecipeStep } from '../../../../types/recipes-types';
import IconLeftArrow from '../../../../components/Icons/IconLeftArrow/IconLeftArrow';
import { generateStepName } from '../../utils';

type ComponentProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const switchStepOrders = (first: IRecipeStep, scnd: IRecipeStep): IReorderedRecipeStep[] => {
  const data: IReorderedRecipeStep[] = [
    { id: first.id, order: scnd.order },
    { id: scnd.id, order: first.order },
  ];

  return data;
};

const RecipeSteps: React.FC<ComponentProps> = ({
  steps,
  getRecipeSteps,
  removeStep,
  reorderSteps,
}) => {
  const [deleteRecipeIndex, setDeleteRecipeIndex] =
    useState<number>(undefinedIndex);
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();

  const navigate = useNavigate();

  const onCreateClickHandler = useCallback(() => {
    navigate(EDIT_RECIPE_PATHS.CREATE_STEP);
  }, [navigate]);

  const onEditClickHandler = useCallback(
    (stepId: string) => {
      const path = generatePath(editRecipeStepFullPath, {
        id,
        step_id: stepId,
      });
      navigate(path);
    },
    [navigate, id],
  );

  const viewValues = steps.map((item, idx) => ({
    Name: `${idx + 1}. ${generateStepName(item)}`,
    id: item?.id,
    idx,
    order: item.order,
  }));

  const isDisabledBtn = useMemo(() => {
    return steps.length >= stepsLimitCount;
  }, [steps]);

  const loadRecipes = useCallback(async () => {
    setIsLoading(true);
    await getRecipeSteps(id);
    setIsLoading(false);
  }, [getRecipeSteps, id]);

  const onDeleteClickHandler = (_id: string, payload: (typeof viewValues)[number]) => {
    setDeleteRecipeIndex(payload.idx);
  };

  const onChangeOrderHandler = async (first: IRecipeStep, scnd: IRecipeStep) => {
    const data: IReorderedRecipeStep[] = switchStepOrders(first, scnd);

    await reorderSteps({ recipeid: id, steps: data });
    loadRecipes();
  };

  const moveUpHanlder = async (_stepId: string, item: (typeof viewValues)[number]) => {
    const changedOrder = item.order - 1;
    const changedIndex = item.idx - 1;

    if (changedOrder < 1 || changedIndex < 0) {
      return;
    }

    const curItem = steps[item.idx];
    const upItem = steps[item.idx - 1];

    await onChangeOrderHandler(curItem, upItem);
  };

  const moveDownHanlder = async (_stepId: string, item: (typeof viewValues)[number]) => {
    const changedOrder = item.order + 1;
    const changedIndex = item.idx + 1;
    if (changedOrder > stepsLimitCount || changedIndex >= steps.length) {
      return;
    }

    const curItem = steps[item.idx];
    const downItem = steps[item.idx + 1];

    await onChangeOrderHandler(curItem, downItem);
  };

  const onCloseModal = () => {
    setDeleteRecipeIndex(undefinedIndex);
  };

  const onAcceptModal = useCallback(async () => {
    await removeStep({ recipeid: id, stepId: steps[deleteRecipeIndex].id });
    loadRecipes();
    onCloseModal();
  }, [loadRecipes, id, deleteRecipeIndex]);

  useEffect(() => {
    loadRecipes();
    // const intervalId = setInterval(loadRecipes, 10000);
    // return () => clearInterval(intervalId);
  }, [loadRecipes]);

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />,
      onClick: onEditClickHandler,
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => <IconBin fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />,
      onClick: onDeleteClickHandler,
    },
    {
      icon: () => (
        <div style={{ transform: 'rotate(90deg)' }}>
          <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
        </div>
      ),
      onClick: moveUpHanlder,
    },

    {
      icon: () => (
        <div style={{ transform: 'rotate(-90deg)' }}>
          <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
        </div>
      ),
      onClick: moveDownHanlder,
    },
  ];

  return (
    <div className="p-recipe-steps flex h-full w-full flex-col">
      <div className="mb-5 flex w-full items-center justify-between">
        <h2 className="text-title-md2 font-semibold text-black dark:text-white">Recipe Steps</h2>
        <InteractiveButton text="Create Step" onClick={onCreateClickHandler} disabled={isDisabledBtn} />
      </div>
      {!steps.length && isLoading ? (
        <div className="flex w-full justify-center">
          <SpinnerLoader />
        </div>
      ) : (
        <>
          <Table values={viewValues} defaultKeys={['Name']} actionsButtons={actionsButtons} />
          <Modal
            modalTitle="Recipe Step Deletion"
            isVisible={deleteRecipeIndex >= 0}
            content={`Are you sure you want to delete the '${
              deleteRecipeIndex >= 0 && generateStepName(steps[deleteRecipeIndex])
            }' step?`}
            acceptBtnText="Delete"
            onClose={onCloseModal}
            onAccept={onAcceptModal}
            showAcceptBtn
            showCancelBtn
          />
        </>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  steps: state.recipes.steps,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getRecipeSteps: dispatch.recipes.getRecipeSteps,
  removeStep: dispatch.recipes.removeStep,
  reorderSteps: dispatch.recipes.reorderSteps,
});

export default connect(mapState, mapDispatch)(RecipeSteps);
